import { Toplist } from "./toplist_criteria";
export var QuestionType;
(function (QuestionType) {
    QuestionType[QuestionType["Alternatives"] = 0] = "Alternatives";
    QuestionType[QuestionType["Autocomplete"] = 1] = "Autocomplete";
    QuestionType[QuestionType["TravelTime"] = 2] = "TravelTime";
    QuestionType[QuestionType["IntegerSlider"] = 3] = "IntegerSlider";
})(QuestionType || (QuestionType = {}));
export class AnswerAlternatives {
    constructor(selected = new Set()) {
        this.selected = selected;
    }
}
export class AnswerNumber {
    constructor(selected = null) {
        this.selected = null;
        this.selected = selected;
    }
}
export function toURLParamValue(q, excludeParametersIrrelevantToSearch = false, distinguishSkippedAndEmpty = false) {
    if (excludeParametersIrrelevantToSearch && (q.question_id == QuestionID.PreviouslyVisitedResorts
        || q.question_id == QuestionID.SingleResortRating
        || isResortComparisonQuestion(q.question_id))) {
        return null;
    }
    if (!q.answer) {
        if (distinguishSkippedAndEmpty) {
            return "skip";
        }
    }
    else if (q.answer instanceof AnswerAlternatives) {
        if (q.answer.selected.size > 0) {
            return Array.from(q.answer.selected.values()).map(x => x.toString()).join(" ");
        }
        else if (distinguishSkippedAndEmpty) {
            return "none";
        }
    }
    else if (q.answer instanceof AnswerNumber) {
        if (q.answer.selected == null) {
            if (distinguishSkippedAndEmpty) {
                return "skip";
            }
        }
        else {
            return q.answer.selected.toString();
        }
    }
    return null;
}
export class AnsweredQuestions {
    constructor() {
        this.questions = [];
    }
    hasQuestionID(id) {
        return this.questions.some(x => x.question_id == id);
    }
    tryGetByID(id) {
        const res = this.questions.find(x => x.question_id == id);
        if (res === undefined) {
            return null;
        }
        else {
            return res;
        }
    }
    getByID(id) {
        const res = this.questions.find(x => x.question_id == id);
        if (res === undefined) {
            throw `Could not find question ${id}`;
        }
        else {
            return res;
        }
    }
    toQueryString() {
        let p = this.toURLParams().toString();
        return p != "" ? "?" + p : "";
    }
    toURLParams(excludeParametersIrrelevantToSearch = false, distinguishSkippedAndEmpty = false) {
        let s = new URLSearchParams();
        for (let q of this.questions) {
            const paramValue = toURLParamValue(q, excludeParametersIrrelevantToSearch, distinguishSkippedAndEmpty);
            if (paramValue) {
                s.append(q.question_id.toString(), paramValue);
            }
        }
        return s;
    }
    addDefaultAnswers(questionCollection) {
        for (let i = 0; i < questionCollection.questions.length; i++) {
            const question = questionCollection.questions[i];
            if (this.hasQuestionID(question.id)) {
                continue;
            }
            this.questions.push({
                question_id: questionCollection.questions[i].id,
                answer: getDefaultAnswer(question),
            });
        }
    }
    equals(other) {
        if (this.questions.length != other.questions.length) {
            return false;
        }
        for (let i = 0; i < this.questions.length; i += 1) {
            const questionId = this.questions[i].question_id;
            const otherQuestion = other.tryGetByID(questionId);
            if (!otherQuestion) {
                return false;
            }
            if (!AnsweredQuestions.questionEquals(this.questions[i], otherQuestion)) {
                return false;
            }
        }
        return true;
    }
    static questionEquals(x, y) {
        if (x.question_id != y.question_id) {
            return false;
        }
        if (!AnsweredQuestions.answerEquals(x.answer, y.answer)) {
            return false;
        }
        return true;
    }
    static answerEquals(x, y) {
        if (!x) {
            return !!y;
        }
        if (!y) {
            return false;
        }
        if (x.selected instanceof Set && y.selected instanceof Set) {
            if (x.selected.size != y.selected.size) {
                return false;
            }
            for (let i of x.selected.values()) {
                if (!y.selected.has(i)) {
                    return false;
                }
            }
            return true;
        }
        return x.selected == y.selected;
    }
    static fromURLParams(params, questionCollection) {
        const res = new AnsweredQuestions();
        for (let [id, values] of params) {
            try {
                const question_id = parseInt(id);
                const question = questionCollection.questions.find(x => x.id == question_id);
                if (!question) {
                    continue;
                }
                if (res.tryGetByID(question_id) !== null) {
                    // For unknown reasons the URL contained multiple entries for a single question.
                    // This is not valid. So we skip it.
                    continue;
                }
                if (values == "skip") {
                    res.questions.push({
                        question_id,
                        answer: null,
                    });
                }
                else if (question.type == QuestionType.Alternatives) {
                    const answers = values.length > 0 && values != "none" ? new Set(values.split(" ").map(x => parseInt(x))) : new Set();
                    res.questions.push({
                        question_id,
                        answer: new AnswerAlternatives(answers),
                    });
                }
                else if (question.type == QuestionType.Autocomplete || question.type == QuestionType.TravelTime || question.type == QuestionType.IntegerSlider) {
                    const answer = values.length > 0 ? parseInt(values) : null;
                    res.questions.push({
                        question_id,
                        answer: new AnswerNumber(answer),
                    });
                }
            }
            catch (e) {
                // Ignore this question
                console.log(`Failed to parse URL parameter '${id}=${values}'. ${e}`);
            }
        }
        return res;
    }
    static fromTopListUri(m, questionCollection) {
        var _a;
        const res = new AnsweredQuestions();
        if (m.property == Toplist.OffPiste) {
            res.questions.push({
                question_id: QuestionID.ImportantSlopeAttributes,
                answer: new AnswerAlternatives(new Set([AnswerID.OffPiste]))
            });
        }
        else if (m.property == Toplist.BeginnerFriendly) {
            res.questions.push({
                question_id: QuestionID.ImportantSlopeAttributes,
                answer: new AnswerAlternatives(new Set([AnswerID.BeginnerFriendly]))
            });
        }
        else if (m.property == Toplist.ChildFriendly) {
            res.questions.push({
                question_id: QuestionID.OtherRecreation,
                answer: new AnswerAlternatives(new Set([AnswerID.ChildFriendly]))
            });
        }
        else if (m.property == Toplist.ToughSlopes) {
            res.questions.push({
                question_id: QuestionID.ImportantSlopeAttributes,
                answer: new AnswerAlternatives(new Set([AnswerID.ToughSlopes]))
            });
        }
        else if (m.property == Toplist.LongWindingSlopes) {
            res.questions.push({
                question_id: QuestionID.ImportantSlopeAttributes,
                answer: new AnswerAlternatives(new Set([AnswerID.LongWindingSlopes]))
            });
        }
        else if (m.property == Toplist.Park) {
            res.questions.push({
                question_id: QuestionID.ImportantSlopeAttributes,
                answer: new AnswerAlternatives(new Set([AnswerID.Park]))
            });
        }
        else if (m.property == Toplist.CrossCountry) {
            res.questions.push({
                question_id: QuestionID.ImportantSlopeAttributes,
                answer: new AnswerAlternatives(new Set([AnswerID.CrossCountry]))
            });
        }
        else if (m.property == Toplist.AfterSki) {
            res.questions.push({
                question_id: QuestionID.OtherRecreation,
                answer: new AnswerAlternatives(new Set([AnswerID.AfterSki]))
            });
        }
        if (m.location != null && m.location != "sverige") {
            let cityLabel = null;
            switch (m.location) {
                case "stockholm":
                    cityLabel = "Stockholm";
                    break;
                case "göteborg":
                    cityLabel = "Göteborg";
                    break;
                case "malmö":
                    cityLabel = "Malmö";
                    break;
            }
            if (cityLabel) {
                const originQuestion = questionCollection.questions.find(x => x.id == QuestionID.Origin);
                if ((originQuestion === null || originQuestion === void 0 ? void 0 : originQuestion.type) == QuestionType.Autocomplete) {
                    const cityId = (_a = originQuestion === null || originQuestion === void 0 ? void 0 : originQuestion.alternatives.find(x => x.label == cityLabel)) === null || _a === void 0 ? void 0 : _a.id;
                    if (cityId) {
                        res.questions.push({
                            question_id: QuestionID.Origin,
                            answer: new AnswerNumber(cityId)
                        });
                        res.questions.push({
                            question_id: QuestionID.Transportation,
                            answer: new AnswerAlternatives(new Set([AnswerID.Car]))
                        });
                        res.questions.push({
                            question_id: QuestionID.TravelTime,
                            answer: new AnswerNumber(6 * 60 * 60), // Default to 6 hours travel time
                        });
                    }
                }
            }
        }
        return res;
    }
}
export var QuestionID;
(function (QuestionID) {
    QuestionID[QuestionID["ImportantSlopeAttributes"] = 0] = "ImportantSlopeAttributes";
    QuestionID[QuestionID["OtherRecreation"] = 1] = "OtherRecreation";
    QuestionID[QuestionID["Food"] = 2] = "Food";
    QuestionID[QuestionID["Date"] = 3] = "Date";
    QuestionID[QuestionID["StayDuration"] = 4] = "StayDuration";
    QuestionID[QuestionID["Transportation"] = 5] = "Transportation";
    QuestionID[QuestionID["TravelTime"] = 6] = "TravelTime";
    QuestionID[QuestionID["Accomodation"] = 7] = "Accomodation";
    QuestionID[QuestionID["Origin"] = 8] = "Origin";
    QuestionID[QuestionID["PreviouslyVisitedResorts"] = 9] = "PreviouslyVisitedResorts";
    QuestionID[QuestionID["SingleResortRating"] = 11] = "SingleResortRating";
    QuestionID[QuestionID["WhichIsMostChildFriendly"] = 10] = "WhichIsMostChildFriendly";
    QuestionID[QuestionID["WhichIsMostBeginnerFriendly"] = 12] = "WhichIsMostBeginnerFriendly";
    QuestionID[QuestionID["WhichIsMostExpertFriendly"] = 13] = "WhichIsMostExpertFriendly";
    QuestionID[QuestionID["WhichHasBestLongSlopes"] = 14] = "WhichHasBestLongSlopes";
    QuestionID[QuestionID["WhichHasBestPark"] = 15] = "WhichHasBestPark";
    QuestionID[QuestionID["WhichHasBestCrossCountry"] = 16] = "WhichHasBestCrossCountry";
    QuestionID[QuestionID["WhichHasBestOffpist"] = 17] = "WhichHasBestOffpist";
    QuestionID[QuestionID["WhichHasBestAfterski"] = 18] = "WhichHasBestAfterski";
})(QuestionID || (QuestionID = {}));
export var AnswerID;
(function (AnswerID) {
    AnswerID[AnswerID["BeginnerFriendly"] = 0] = "BeginnerFriendly";
    AnswerID[AnswerID["ChildFriendly"] = 1] = "ChildFriendly";
    AnswerID[AnswerID["ToughSlopes"] = 2] = "ToughSlopes";
    AnswerID[AnswerID["LongWindingSlopes"] = 3] = "LongWindingSlopes";
    AnswerID[AnswerID["Park"] = 4] = "Park";
    AnswerID[AnswerID["CrossCountry"] = 5] = "CrossCountry";
    AnswerID[AnswerID["OffPiste"] = 6] = "OffPiste";
    AnswerID[AnswerID["AfterSki"] = 7] = "AfterSki";
    AnswerID[AnswerID["Spa"] = 8] = "Spa";
    AnswerID[AnswerID["Bathhouse"] = 9] = "Bathhouse";
    AnswerID[AnswerID["OwnFood"] = 10] = "OwnFood";
    AnswerID[AnswerID["RegularRestaurants"] = 11] = "RegularRestaurants";
    AnswerID[AnswerID["FancyRestaurants"] = 12] = "FancyRestaurants";
    AnswerID[AnswerID["Car"] = 13] = "Car";
    AnswerID[AnswerID["Train"] = 14] = "Train";
    AnswerID[AnswerID["PublicTransport"] = 15] = "PublicTransport";
    AnswerID[AnswerID["Cabin"] = 16] = "Cabin";
    AnswerID[AnswerID["Hotel"] = 17] = "Hotel";
    AnswerID[AnswerID["OtherAccomodation"] = 18] = "OtherAccomodation";
    AnswerID[AnswerID["NoneOfThese"] = 19] = "NoneOfThese";
})(AnswerID || (AnswerID = {}));
export var QuestionGroup;
(function (QuestionGroup) {
    QuestionGroup[QuestionGroup["SingleQuestion"] = 0] = "SingleQuestion";
    QuestionGroup[QuestionGroup["ResortRanking"] = 1] = "ResortRanking";
})(QuestionGroup || (QuestionGroup = {}));
export function getDefaultAnswer(question) {
    switch (question.type) {
        case QuestionType.Alternatives:
            if (question.id == QuestionID.Transportation) {
                return new AnswerAlternatives(new Set([AnswerID.Car]));
            }
            return new AnswerAlternatives();
        case QuestionType.Autocomplete:
            return new AnswerNumber();
        case QuestionType.TravelTime:
            return new AnswerNumber(6 * 60 * 60);
        case QuestionType.IntegerSlider:
            return new AnswerNumber(4);
    }
}
export const resortComparisonQuestions = new Set([
    QuestionID.WhichIsMostChildFriendly,
    QuestionID.WhichIsMostBeginnerFriendly,
    QuestionID.WhichIsMostExpertFriendly,
    QuestionID.WhichHasBestLongSlopes,
    QuestionID.WhichHasBestPark,
    QuestionID.WhichHasBestCrossCountry,
    QuestionID.WhichHasBestOffpist,
    QuestionID.WhichHasBestAfterski,
]);
export function isResortComparisonQuestion(questionId) {
    return resortComparisonQuestions.has(questionId);
}
export function getPreferredResortRankingTopics(answered) {
    var _a, _b;
    const preferredResortRankingTopics = [];
    const importantSlopeAttributes = (_a = answered.tryGetByID(QuestionID.ImportantSlopeAttributes)) === null || _a === void 0 ? void 0 : _a.answer;
    if (importantSlopeAttributes && importantSlopeAttributes.selected instanceof Set) {
        for (let a of importantSlopeAttributes.selected) {
            preferredResortRankingTopics.push(a);
        }
    }
    const otherRecreation = (_b = answered.tryGetByID(QuestionID.OtherRecreation)) === null || _b === void 0 ? void 0 : _b.answer;
    if (otherRecreation && otherRecreation.selected instanceof Set) {
        for (let a of otherRecreation.selected) {
            preferredResortRankingTopics.push(a);
        }
    }
    return preferredResortRankingTopics;
}
export function getPreferredResortComparisonQuestions(answered) {
    var _a, _b;
    const preferredResortComparisonQuestions = [];
    const importantSlopeAttributes = (_a = answered.tryGetByID(QuestionID.ImportantSlopeAttributes)) === null || _a === void 0 ? void 0 : _a.answer;
    if (importantSlopeAttributes && importantSlopeAttributes.selected instanceof Set) {
        if (importantSlopeAttributes.selected.has(AnswerID.BeginnerFriendly)) {
            preferredResortComparisonQuestions.push(QuestionID.WhichIsMostBeginnerFriendly);
        }
        if (importantSlopeAttributes.selected.has(AnswerID.ToughSlopes)) {
            preferredResortComparisonQuestions.push(QuestionID.WhichIsMostExpertFriendly);
        }
        if (importantSlopeAttributes.selected.has(AnswerID.LongWindingSlopes)) {
            preferredResortComparisonQuestions.push(QuestionID.WhichHasBestLongSlopes);
        }
        if (importantSlopeAttributes.selected.has(AnswerID.Park)) {
            preferredResortComparisonQuestions.push(QuestionID.WhichHasBestPark);
        }
        if (importantSlopeAttributes.selected.has(AnswerID.CrossCountry)) {
            preferredResortComparisonQuestions.push(QuestionID.WhichHasBestCrossCountry);
        }
        if (importantSlopeAttributes.selected.has(AnswerID.OffPiste)) {
            preferredResortComparisonQuestions.push(QuestionID.WhichHasBestOffpist);
        }
    }
    const otherRecreation = (_b = answered.tryGetByID(QuestionID.OtherRecreation)) === null || _b === void 0 ? void 0 : _b.answer;
    if (otherRecreation && otherRecreation.selected instanceof Set) {
        if (otherRecreation.selected.has(AnswerID.AfterSki)) {
            preferredResortComparisonQuestions.push(QuestionID.WhichHasBestAfterski);
        }
        if (otherRecreation.selected.has(AnswerID.ChildFriendly)) {
            preferredResortComparisonQuestions.push(QuestionID.WhichIsMostChildFriendly);
        }
    }
    return preferredResortComparisonQuestions;
}
export function storeAnswersInLocalStorage(answers) {
    if (answers == null) {
        localStorage.removeItem("answers");
    }
    else {
        localStorage.setItem("answers", answers.toURLParams().toString());
    }
}
export function loadAnswersFromLocalStorage(questions) {
    let serialized = localStorage.getItem("answers");
    if (serialized === null)
        return null;
    try {
        return AnsweredQuestions.fromURLParams(new URLSearchParams(serialized), questions);
    }
    catch (e) {
        console.warn("Failed to load answers from local storage", e);
    }
    return null;
}
